/* Elysian cart motion and in-place updates (2026-09-26). Loaded after Dawn's cart-drawer.js and cart.js; Dawn still owns the cart API
   calls (/cart/add, /cart/change, /cart/update) and the section rendering. This file only changes how the new HTML lands:
   - the drawer opens and closes with the site's motion (600ms in, 450ms out, cubic-bezier(0.22, 1, 0.36, 1), transform and opacity only),
     contents settle in with a 40ms stagger, the free-shipping fill animates from where it was;
   - updates while the drawer or the cart page is open (quantity, remove, add-on Add) morph in place: rows that leave or arrive animate
     their height, everything else keeps its node position and the scroll position is kept;
   - body scroll locks without layout shift (scrollbar width compensated); focus is trapped, Escape closes, focus returns to the trigger;
   - reduced motion: instant, no stagger, no height or fill animation.
   Progressive enhancement: without this file (or without Element.animate) Dawn's behaviour stands and every control still works. */
(function () {
  'use strict';
  var CartDrawer = window.customElements && customElements.get('cart-drawer');
  var CartItemsEl = window.customElements && customElements.get('cart-items');
  if (!CartDrawer) return;

  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var IN = 600, OUT = 450, CHANGE = 500;
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  function motionOK() { return canAnimate && !(reduceQuery && reduceQuery.matches); }
  function parse(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function norm(html) { return (html || '').replace(/\sview-event-payload="[^"]*"/g, '').replace(/\s+/g, ' ').trim(); }

  /* ---------- Scroll lock: overflow hidden on body (Dawn's class) plus padding for the scrollbar it removes, so nothing moves ---------- */
  function lockScroll() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add('overflow-hidden');
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
  }
  function unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';
  }

  /* ---------- Free-shipping fill: translateX from the previous percent to the new one ---------- */
  function animateShip(root, from, delay, noParty, fromFree) {
    var fill = root && root.querySelector('.ely-ship__fill');
    if (!fill || from == null || !motionOK()) return;
    var to = parseFloat(fill.getAttribute('data-ely-ship'));
    if (isNaN(to) || from === to) return;
    fill.animate(
      [{ transform: 'translateX(' + (from - 100) + '%)' }, { transform: 'translateX(' + (to - 100) + '%)' }],
      { duration: IN, easing: EASE, delay: delay || 0, fill: 'backwards' }
    );
    /* the moment free shipping unlocks (under the threshold before, full now): confetti from the fill's end, as the fill lands.
       2026-10-04: the bar measures one bucket (one-time, or subscriptions in a subscription-only cart; snippets/elysian-cart-shipping.liquid).
       When the bar switches bucket, it only celebrates if its bucket was not already shipping free before the change (removing the
       one-time items from a cart whose subscription already shipped free fills the bar, but nothing new was unlocked). */
    if (noParty || !(from < 100 && to >= 100)) return;
    var mode = shipModeOf(root);
    if (fromFree != null && mode && (' ' + fromFree + ' ').indexOf(' ' + mode + ' ') !== -1) return;
    setTimeout(function () { confetti(fill); }, (delay || 0) + IN * 0.55);
  }

  /* ---------- Confetti (2026-09-28, Chris): ~50 pieces in the brand colours burst up and out from the bar's filled end, fall with
     gravity and a little spin, and are gone in about 1.2s. Self-written, no library: each piece is one span animated with the Web
     Animations API on transform and opacity only (physics sampled into keyframes); one fixed layer over everything, pointer-events
     none, removed when the last piece finishes. Never with reduced motion (animateShip returns before this). ---------- */
  var PARTY = ['#ffcac9', '#fd9999', '#456667', '#223838', '#fff9f4'];
  function confetti(fill) {
    if (!motionOK() || !fill.isConnected) return;
    var track = fill.parentNode.getBoundingClientRect();
    if (!track.width) return;
    var ox = track.right, oy = track.top + track.height / 2;
    var layer = document.createElement('div');
    layer.className = 'ely-confetti';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    var n = 50, left = n, G = 1500, STEPS = 12;
    var end = function () { if (--left <= 0 && layer.parentNode) layer.parentNode.removeChild(layer); };
    for (var i = 0; i < n; i++) {
      var el = document.createElement('i');
      var round = i % 6 === 0;
      var colour = PARTY[i % PARTY.length];
      el.className = 'ely-confetti__bit' + (round ? ' ely-confetti__bit--round' : '') + (colour === '#fff9f4' ? ' ely-confetti__bit--light' : '');
      el.style.background = colour;
      if (!round) { el.style.width = (5 + Math.random() * 3).toFixed(1) + 'px'; el.style.height = (8 + Math.random() * 5).toFixed(1) + 'px'; }
      layer.appendChild(el);
      /* fan up and out, biased to the left since the burst starts at the bar's right end */
      var ang = (-170 + Math.random() * 135) * Math.PI / 180;
      var sp = 320 + Math.random() * 420;
      var vx = Math.cos(ang) * sp, vy = Math.sin(ang) * sp;
      var spin = (Math.random() < 0.5 ? -1 : 1) * (240 + Math.random() * 540);
      var tilt = Math.random() * 360;
      var dur = 1000 + Math.random() * 300;
      var frames = [];
      for (var k = 0; k <= STEPS; k++) {
        var f = k / STEPS, s = f * dur / 1000;
        var x = ox + vx * s * (1 - 0.35 * f), y = oy + vy * s + 0.5 * G * s * s;
        frames.push({
          offset: f,
          transform: 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px) rotate(' + (tilt + spin * s).toFixed(0) + 'deg) scale(' + (f === 0 ? 0.4 : 1) + ')',
          opacity: f < 0.7 ? 1 : (1 - (f - 0.7) / 0.3).toFixed(2)
        });
      }
      var a = el.animate(frames, { duration: dur, easing: 'linear', fill: 'forwards' });
      a.onfinish = end; a.oncancel = end;
    }
  }
  function shipOf(root) {
    var fill = root && root.querySelector('.ely-ship__fill');
    return fill ? parseFloat(fill.getAttribute('data-ely-ship')) : null;
  }
  /* 2026-10-04: which buckets ship free ('one', 'sub', space separated) and which one the bar measures, as the Liquid rendered them */
  function shipFreeOf(root) {
    var fill = root && root.querySelector('.ely-ship__fill');
    return fill ? (fill.getAttribute('data-ely-ship-free') || '') : null;
  }
  function shipModeOf(root) {
    var ship = root && root.querySelector('.ely-ship');
    return ship ? ship.getAttribute('data-ely-ship-mode') : null;
  }

  /* ---------- Morph: snapshot keyed blocks, swap the HTML, then animate what arrived and what left ---------- */
  var ROOT = '__root';
  function keyOf(el) { return el.getAttribute('data-ely-key'); }
  function parentKey(el, root) {
    var p = el.parentElement && el.parentElement.closest('[data-ely-key]');
    return p && root.contains(p) ? keyOf(p) : ROOT;
  }
  function gapOf(parent) {
    var cs = getComputedStyle(parent);
    return { gap: parseFloat(cs.rowGap) || 0, grid: cs.display.indexOf('grid') !== -1 };
  }
  function describeFocus(root) {
    var a = document.activeElement;
    if (!a || a === document.body || !root.contains(a)) return null;
    var row = a.closest('[data-ely-key]');
    return { id: a.id || null, name: a.getAttribute('name'), cls: a.classList[0] || null, row: row ? keyOf(row) : null };
  }
  function restoreFocus(root, f) {
    if (!f) return false;
    var el = null;
    if (f.id) el = document.getElementById(f.id);
    if (!el && f.row) {
      var row = root.querySelector('[data-ely-key="' + f.row + '"]');
      if (row) el = (f.name && row.querySelector('[name="' + f.name + '"]')) || (f.cls && row.querySelector('.' + f.cls));
    }
    if (el && el.focus) { el.focus({ preventScroll: true }); return true; }
    return false;
  }
  function snapshot(root) {
    var s = { keys: {}, order: [], ship: shipOf(root), shipFree: shipFreeOf(root), focus: describeFocus(root), scroll: null };
    var sc = root.querySelector('cart-drawer-items');
    if (sc) s.scroll = sc.scrollTop;
    root.querySelectorAll('[data-ely-key]').forEach(function (el) {
      var k = keyOf(el);
      s.keys[k] = { el: el, h: el.getBoundingClientRect().height, parent: parentKey(el, root) };
      s.order.push(k);
    });
    return s;
  }
  function stripGhost(el) {
    el.removeAttribute('id');
    el.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
    el.querySelectorAll('input[name], textarea[name], select[name]').forEach(function (n) { n.removeAttribute('name'); n.disabled = true; });
    el.removeAttribute('data-ely-key');
    el.querySelectorAll('[data-ely-key]').forEach(function (n) { n.removeAttribute('data-ely-key'); });
    el.classList.remove('cart-item');
    el.setAttribute('aria-hidden', 'true');
    el.inert = true;
    el.style.pointerEvents = 'none';
    el.style.overflow = 'hidden';
    el.style.animation = 'none';
  }
  function exitEl(el, h, parent, next) {
    stripGhost(el);
    parent.insertBefore(el, next || null);
    var g = gapOf(parent);
    var frames = g.grid
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [{ height: h + 'px', marginBottom: '0px', opacity: 1 }, { height: '0px', marginBottom: -g.gap + 'px', opacity: 0 }];
    var anim = el.animate(frames, { duration: g.grid ? OUT * 0.6 : OUT, easing: EASE, fill: 'forwards' });
    var done = function () { if (el.parentNode) el.parentNode.removeChild(el); };
    anim.onfinish = done;
    anim.oncancel = done;
  }
  function enterEl(el) {
    var parent = el.parentElement;
    if (!parent) return;
    var g = gapOf(parent);
    var h = el.getBoundingClientRect().height;
    el.style.animation = 'none';
    var frames = g.grid
      ? [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }]
      : [
          { height: '0px', marginBottom: -g.gap + 'px', opacity: 0, transform: 'translateY(8px)' },
          { height: h + 'px', marginBottom: '0px', opacity: 1, transform: 'none' }
        ];
    if (!g.grid) el.style.overflow = 'hidden';
    var anim = el.animate(frames, { duration: CHANGE, easing: EASE });
    var done = function () { el.style.overflow = ''; };
    anim.onfinish = done;
    anim.oncancel = done;
  }
  function settle(root, s) {
    var sc = root.querySelector('cart-drawer-items');
    if (sc && s.scroll) sc.scrollTop = s.scroll;
    animateShip(root, s.ship, 0, false, s.shipFree);
    if (!motionOK()) return;
    var now = {};
    now[ROOT] = root;
    root.querySelectorAll('[data-ely-key]').forEach(function (el) { now[keyOf(el)] = el; });
    var had = function (k) { return k === ROOT || !!s.keys[k]; };
    /* the last line left (or the first arrived): a new state, not an edit; the new content fades up, nothing collapses */
    if (!!s.keys.lines !== !!now.lines) {
      var fresh = root.querySelector('.ely-cart-empty') || root.querySelector('[data-ely-key]');
      if (fresh) fresh.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: CHANGE, easing: EASE });
      return;
    }
    Object.keys(now).forEach(function (k) {
      if (k === ROOT || s.keys[k]) return;
      if (!had(parentKey(now[k], root))) return; /* an ancestor is arriving too; it animates for both */
      enterEl(now[k]);
    });
    s.order.forEach(function (k, i) {
      if (now[k]) return;
      var o = s.keys[k];
      if (!now[o.parent]) return; /* its parent left too */
      var next = null;
      for (var j = i + 1; j < s.order.length; j++) {
        var k2 = s.order[j];
        if (s.keys[k2].parent === o.parent && now[k2]) { next = now[k2]; break; }
      }
      if (!next && o.parent === ROOT) return; /* no anchor inside the root: let it go */
      exitEl(o.el, o.h, now[o.parent], next);
    });
  }
  function morph(root, html) {
    var s = snapshot(root);
    root.innerHTML = html;
    settle(root, s);
    return s;
  }

  /* ---------- Drawer: open and close with the site's motion ---------- */
  var proto = CartDrawer.prototype;
  var dawnRender = proto.renderContents;

  proto.open = function (triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    var note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role') && note.nextElementSibling && note.nextElementSibling.id) this.setSummaryAccessibility(note);
    var self = this;
    var still = !motionOK();
    clearTimeout(this._elyOpenT); clearTimeout(this._elyMoveT); clearTimeout(this._elyFocusT);
    lockScroll();
    if (!still) this.classList.add('ely-drawer--opening', 'ely-drawer--moving');
    void this.offsetWidth; /* commit the closed state so the transition always runs (Dawn used a timeout for the same reason) */
    this.classList.add('animate', 'active');
    var fromAdd = this._elyShipFrom != null; /* an add opened the drawer; a plain open never celebrates */
    var from = fromAdd ? this._elyShipFrom : 0;
    var fromFree = fromAdd ? this._elyShipFreeFrom : null;
    this._elyShipFrom = null;
    this._elyShipFreeFrom = null;
    animateShip(this, from, 200, !fromAdd, fromFree);
    var trap = function () {
      var container = self.classList.contains('is-empty') ? self.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      var panel = self.querySelector('.drawer__inner');
      if (typeof trapFocus === 'function') trapFocus(container || self, panel);
      else if (panel) panel.focus();
    };
    if (still) trap();
    else {
      this._elyFocusT = setTimeout(trap, IN);
      this._elyMoveT = setTimeout(function () { self.classList.remove('ely-drawer--moving'); }, IN + 50);
      this._elyOpenT = setTimeout(function () { self.classList.remove('ely-drawer--opening'); }, 1600);
    }
    var items = this.querySelector('cart-drawer-items');
    if (items && typeof items.dispatchViewEvent === 'function') items.dispatchViewEvent();
  };

  proto.close = function () {
    if (!this.classList.contains('active')) return;
    var self = this;
    clearTimeout(this._elyOpenT); clearTimeout(this._elyFocusT); clearTimeout(this._elyMoveT);
    this.classList.remove('active', 'ely-drawer--opening');
    if (motionOK()) {
      this.classList.add('ely-drawer--moving');
      this._elyMoveT = setTimeout(function () { self.classList.remove('ely-drawer--moving'); }, OUT + 50);
    }
    var back = this.activeElement;
    if (!back || !back.isConnected) back = document.getElementById('cart-icon-bubble');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(back);
    else if (back && back.focus) back.focus();
    unlockScroll();
  };

  /* Which form sent the add: Safari does not focus a clicked button, so document.activeElement cannot tell a cart-page add-on apart */
  var lastSubmit = null;
  document.addEventListener('submit', function (e) { lastSubmit = e.target; }, true);

  /* Adds (Dawn's product-form calls renderContents with the add response and its rendered sections) */
  proto.renderContents = function (parsedState) {
    var sections = (parsedState && parsedState.sections) || {};
    var html = sections['cart-drawer'];
    var inner = this.querySelector('.drawer__inner');
    if (!html || !inner) return dawnRender.call(this, parsedState);
    var doc = parse(html);
    var next = doc.querySelector('.drawer__inner');
    if (!next) return dawnRender.call(this, parsedState);
    var nextDrawer = doc.querySelector('cart-drawer');
    this.productId = parsedState.id;

    var bubbleHTML = sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) {
      var b = parse(bubbleHTML).querySelector('.shopify-section');
      if (b) bubble.innerHTML = b.innerHTML;
    }
    if (nextDrawer) this.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    inner.classList.remove('is-empty');

    var origin = lastSubmit || this.activeElement;
    lastSubmit = null;
    var fromCartPage = origin && origin.closest && origin.closest('cart-items');
    this._elyLast = norm(next.innerHTML);

    if (this.classList.contains('active')) {
      morph(inner, next.innerHTML);
      focusAdded(this, parsedState);
      return;
    }
    this._elyShipFrom = shipOf(inner);
    this._elyShipFreeFrom = shipFreeOf(inner);
    if (this._elyShipFrom == null) this._elyShipFrom = 0;
    inner.innerHTML = next.innerHTML;
    if (fromCartPage) { this._elyShipFrom = null; return; } /* add-on added on the cart page: the page updates in place, the drawer stays shut */
    this.open();
  };

  function focusAdded(drawer, parsedState) {
    var vid = parsedState && (parsedState.variant_id || parsedState.id);
    var row = vid && drawer.querySelector('.ely-line[data-variant-id="' + vid + '"]');
    var target = row ? row.querySelector('.ely-line__name') : drawer.querySelector('.drawer__inner');
    if (typeof trapFocus === 'function') trapFocus(document.getElementById('CartDrawer') || drawer, target);
    else if (target) target.focus();
  }

  /* A fresh render of the drawer from the server (after any cart change made elsewhere); a no-op when nothing changed */
  proto.elyRefresh = function () {
    var self = this;
    if (!window.routes) return Promise.resolve();
    return fetch(window.routes.cart_url + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = parse(text);
        var next = doc.querySelector('.drawer__inner');
        var inner = self.querySelector('.drawer__inner');
        if (!next || !inner) return;
        var n = norm(next.innerHTML);
        if (n === self._elyLast) return;
        self._elyLast = n;
        var nextDrawer = doc.querySelector('cart-drawer');
        if (nextDrawer) self.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
        if (self.classList.contains('active')) {
          var s = morph(inner, next.innerHTML);
          restoreFocus(inner, s.focus);
        } else {
          inner.innerHTML = next.innerHTML;
        }
      })
      .catch(function () {});
  };

  /* Focus trap. Dawn's trapFocus relies on the browser's own Tab order reaching the last element, which Safari's default settings skip
     (Tab there visits form fields only), so focus could leave the dialog. While the drawer is open, Tab and Shift+Tab step through the
     drawer's visible controls here and wrap; focus that lands outside the open drawer is brought back to the panel. */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex^="-"])';
  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
      if (el.closest('details:not([open])') && el.tagName !== 'SUMMARY') return false;
      return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    });
  }
  window.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var d = document.querySelector('cart-drawer.active');
    if (!d) return;
    var panel = d.querySelector('.drawer__inner');
    var list = focusables(panel);
    if (!list.length) { e.preventDefault(); panel.focus(); return; }
    var i = list.indexOf(document.activeElement);
    var next = e.shiftKey ? (i <= 0 ? list[list.length - 1] : list[i - 1]) : (i === -1 || i === list.length - 1 ? list[0] : list[i + 1]);
    e.preventDefault();
    e.stopImmediatePropagation();
    next.focus();
  }, true);
  document.addEventListener('focusin', function (e) {
    var d = document.querySelector('cart-drawer.active');
    if (!d || d.contains(e.target)) return;
    var panel = d.querySelector('.drawer__inner');
    if (panel) panel.focus({ preventScroll: true });
  });

  /* Escape closes from anywhere while open (Dawn listens for keyup inside the drawer only) */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var d = document.querySelector('cart-drawer.active');
    if (!d) return;
    var openDetails = e.target && e.target.closest && e.target.closest('details[open]');
    if (openDetails && d.contains(openDetails)) return; /* Escape first closes an open note, as in Dawn */
    d.close();
  });

  /* ---------- Cart items (drawer and page): Dawn's updateQuantity swaps the HTML; take the snapshot just before it ---------- */
  if (!CartItemsEl) return;
  var ciProto = CartItemsEl.prototype;
  var dawnSections = ciProto.getSectionsToRender;
  var dawnOnCartUpdate = ciProto.onCartUpdate;
  var PAGE_REGIONS = ['ely-cart-items-region', 'ely-cart-summary-top', 'ely-cart-summary-bottom'];

  ciProto.getSectionInnerHTML = function (html, selector) {
    var doc = parse(html);
    var src = doc.querySelector(selector);
    var out = src ? src.innerHTML : '';
    var root = null;
    if (selector === '.drawer__inner') {
      var drawer = document.querySelector('cart-drawer');
      root = drawer && drawer.querySelector('.drawer__inner');
      if (drawer) drawer._elyLast = norm(out);
    } else if (selector && selector.charAt(0) === '#' && PAGE_REGIONS.indexOf(selector.slice(1)) !== -1) {
      root = document.getElementById(selector.slice(1));
    }
    if (root) {
      var s = snapshot(root);
      var q = window.queueMicrotask || function (fn) { Promise.resolve().then(fn); };
      q(function () { if (root.isConnected) settle(root, s); });
    }
    return out;
  };

  /* Cart page: three regions re-render (items, summary top, summary bottom) */
  ciProto.getSectionsToRender = function () {
    if (this.tagName !== 'CART-ITEMS' || !document.getElementById('ely-cart-items-region')) return dawnSections.call(this);
    var holder = document.getElementById('main-cart-items');
    var id = holder && holder.dataset.id;
    var list = PAGE_REGIONS.map(function (r) { return { id: r, section: id, selector: '#' + r }; });
    list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    list.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    return list;
  };

  function refreshPage(el) {
    var holder = document.getElementById('main-cart-items');
    if (!holder || !window.routes) return Promise.resolve();
    return fetch(window.routes.cart_url + '?section_id=' + holder.dataset.id)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = parse(text);
        PAGE_REGIONS.forEach(function (r) {
          var cur = document.getElementById(r), nxt = doc.getElementById(r);
          if (!cur || !nxt) return;
          if (norm(cur.innerHTML) === norm(nxt.innerHTML)) return;
          var s = morph(cur, nxt.innerHTML);
          restoreFocus(cur, s.focus);
        });
        var nextItems = doc.querySelector('cart-items');
        if (el && nextItems) el.classList.toggle('is-empty', nextItems.classList.contains('is-empty'));
      })
      .catch(function () {});
  }

  ciProto.onCartUpdate = function () {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      var drawer = document.querySelector('cart-drawer');
      return drawer && drawer.elyRefresh ? drawer.elyRefresh() : dawnOnCartUpdate.call(this);
    }
    if (document.getElementById('ely-cart-items-region')) return refreshPage(this);
    return dawnOnCartUpdate.call(this);
  };

  /* On the cart page, a change made in the drawer (source cart-items, which Dawn's page listener ignores) refreshes the page, and a change
     made on the page refreshes the closed drawer; both are no-ops when nothing changed */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'cart-items') return;
      var page = document.querySelector('cart-items.ely-cart-page');
      var drawer = document.querySelector('cart-drawer');
      if (page) refreshPage(page);
      if (drawer && page) drawer.elyRefresh();
    });
  }
})();

/* 2026-10-20: Recharge add-on guard. On a product page with selling plans, Recharge's widget mounts into the first /cart/add form, which
   can be a cart add-on (drawer or cart page, .ely-upsell) or the product page add-ons strip (.ely-addons), and injects an enabled
   selling_plan select. The add-on variant has no plan, so Add failed with 422 "Cannot apply selling plan to variant". Add-ons are one-time
   items: disable every selling_plan field and every named field inside a Recharge container in those forms, now, whenever Recharge mounts
   late or the drawer re-renders (observer scoped to the drawer, the cart page items and the add-ons strip), and in the capture phase of
   submit, before Dawn's product-form builds its FormData. Same approach as quietRecharge() in elysian-product.js. Separate from the drawer
   code above, so it runs even where that returns early. */
(function () {
  'use strict';
  var SCOPE = '.ely-upsell form, .ely-addons form';
  var RC = '.rc-container-wrapper, .rc_container_wrapper, .rc-container, .rc_container';
  function quietForm(form) {
    var els = form.elements;
    if (!els) return;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.name || el.disabled) continue;
      if (el.name === 'selling_plan' || (el.closest && el.closest(RC))) el.disabled = true;
    }
  }
  function quietAll() {
    var forms = document.querySelectorAll(SCOPE);
    for (var i = 0; i < forms.length; i++) quietForm(forms[i]);
  }
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.closest && f.closest('.ely-upsell, .ely-addons')) quietForm(f);
  }, true);
  function start() {
    quietAll();
    if (!window.MutationObserver) return;
    var mo = new MutationObserver(quietAll);
    var roots = document.querySelectorAll('cart-drawer, cart-items, .ely-addons');
    for (var i = 0; i < roots.length; i++) mo.observe(roots[i], { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* 2026-10-26: cart note summary. Shopify's checkout cannot show the order note, so the cart shows it back, right above Check Out
   (snippets/elysian-cart-note-confirm.liquid). Liquid renders it from cart.note (reload, reopen, JS off); here it repaints whenever
   cart.js saves the note ('ely:cart-note', after /cart/update.js answers), using textContent only, so a note can never inject HTML.
   Edit opens the note field, scrolls it into view (instant under reduced motion) and puts the caret at the end. */
(function () {
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function paint(note) {
    var v = (note || '').trim();
    document.querySelectorAll('[data-ely-note-confirm]').forEach(function (box) {
      var t = box.querySelector('[data-ely-note-text]');
      if (t) t.textContent = v; /* the quotation marks are CSS (::before / ::after) */
      box.hidden = !v;
    });
  }
  document.addEventListener('ely:cart-note', function (e) { paint(e.detail && e.detail.note); });
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('[data-ely-note-edit]');
    if (!a) return;
    var field = document.getElementById(a.getAttribute('aria-controls') || '');
    if (!field) return;
    e.preventDefault();
    var d = field.closest('details');
    if (d && !d.open) {
      d.open = true;
      var sum = d.querySelector('summary');
      if (sum && sum.hasAttribute('aria-expanded')) sum.setAttribute('aria-expanded', 'true');
    }
    try { field.focus({ preventScroll: true }); } catch (_) { field.focus(); }
    try { field.scrollIntoView({ block: 'center', behavior: reduce && reduce.matches ? 'auto' : 'smooth' }); } catch (_) { field.scrollIntoView(); }
    var n = field.value.length;
    try { field.setSelectionRange(n, n); } catch (_) {}
  });
})();
